import type { ButtonProps, ShorthandCollection } from '@archipro-design/aria';
import { NavigationList, useStyles } from '@archipro-design/aria';
import { Link, useLocation } from '@remix-run/react';
import {
    LeftSideContent,
    PreviewTag,
    PreviewItem,
} from '~/modules/design-board/component/page-layout/PageLayout.style';
import type { SideNavItems } from '~/modules/design-board/type/design-board-types';
import {
    PROJECT_INDEX_PAGE_SIDE_ITEMS,
    DEFAULT_SIDE_NAV_ITEM,
    ALL_SAVED_PIN_NAV_ITEM,
    DESIGN_BOARD_HOME_LINK,
} from '~/modules/design-board/config';
import { useMatchesHandleData } from '~/modules/root';
import type { DesignBoardPinTargetType } from 'generated/graphql';

interface LeftSideNavProps {
    topItem?: SideNavItems;
    navItems: SideNavItems[];
    targetType?: DesignBoardPinTargetType;
    onPreviewItemClick?: () => void;
    previewTag?: string;
}

const isOldStackLink = (link: string) => {
    return (
        PROJECT_INDEX_PAGE_SIDE_ITEMS.find((item) => item.link === link) &&
        link !== DEFAULT_SIDE_NAV_ITEM.link
    );
};

const LeftSideNav = ({
    topItem,
    navItems,
    targetType,
    onPreviewItemClick,
    previewTag,
}: LeftSideNavProps) => {
    const location = useLocation();

    const isBoardDetailPage = useMatchesHandleData('isBoardDetailPage', false);

    const { css } = useStyles({
        hasTitle: !!topItem?.title,
        isBoardDetailPage,
    });

    const navigationListItems = navItems.map((item) => {
        const isHomeLink = item.link === DESIGN_BOARD_HOME_LINK;
        const isActive =
            isHomeLink ||
            (targetType && item.type === targetType) ||
            item.link.includes(
                isBoardDetailPage
                    ? location.search || ALL_SAVED_PIN_NAV_ITEM.link
                    : location.pathname
            );
        const titleNode = isActive ? (
            <span className="active">{item.title}</span>
        ) : (
            <span className={item.preview ? css(PreviewItem) : ''}>
                {item.title}
                {item.preview && (
                    <span className={css(PreviewTag)}>
                        {previewTag ?? 'soon'}
                    </span>
                )}
            </span>
        );

        if (item.preview) {
            return {
                as: 'div',
                children: titleNode,
                onClick: () => onPreviewItemClick?.(),
            };
        }

        if (isOldStackLink(item.link)) {
            return {
                as: 'a',
                children: titleNode,
                href: item.link,
            };
        }

        return {
            as: Link,
            children: titleNode,
            to: item.link,
            prefetch: 'intent',
        };
    }) as ShorthandCollection<ButtonProps>;

    return (
        <div className={css(LeftSideContent)}>
            <NavigationList items={navigationListItems} />
        </div>
    );
};

export default LeftSideNav;
